import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";
import useEnrolPageProps from "@hooks/useEnrolPageProps";
import usePostCheckoutState from "@hooks/usePostCheckoutState";

import { ReCaptcha } from "@/components/ReCaptcha";
import useSettingsPageProps from "@/hooks/useSettingsPageProps";
import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { UserInterface } from "@/ts/interfaces/Parent/UserInterface";
import { dispatchHSEvents } from "@/utils/events/dispatchHSEevents";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePage } from "@inertiajs/react";

import { useAtom, useSetAtom } from "jotai";
import * as Yup from "yup";

import {
    Checkbox,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Link
} from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { CheckoutPagesEnum } from "@ts/enums/CheckoutPagesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { FLAG_STATUS } from "@ts/enums/SharedEnums";
import { AddonInterface } from "@ts/interfaces/Checkout/AddonInterface";
import { GradeInterface } from "@ts/interfaces/Checkout/GradeInterface";
import { PlanInterface } from "@ts/interfaces/Checkout/PlanInterface";
import { ProductFamilyInterface } from "@ts/interfaces/Checkout/ProductFamilyInterface";

import {
    canShowExistingUserPageAtom,
    checkoutAtom,
    couponAtom,
    isNewsletterSubEnabledAtom,
    isProvidedQueryParamsAtom,
    pageQueryParamsAtom,
    parentIdAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom";

import { checkExistingUserApi, validateEmailApi } from "@api/checkoutApi.ts";

import { mapQueryParamsDataToCheckoutAtom } from "@utils/checkoutDataMapper";
import { dispatchGAEvents } from "@utils/events/dispatchGAEvents";
import { getTrackingId, nameRegex, removeNullProperties } from "@utils/utils";

import ContentLayout from "@layouts/ContentLayout";

import { Button } from "@components/Button";
import { Text } from "@components/Text";

interface UserInfoFormData {
    firstName: string;
    email: string;
}

const UserInfoContainer = () => {
    const { hubspotEventCheckout, emailAbstractApiKey, parentUser } =
        useCheckoutPageProps();
    const { hubspotEventEnrol } = useEnrolPageProps();

    const { t, i18n } = useTranslation();

    const isParentUserLoggedIn = !!parentUser;

    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const productFamilyId = searchParams.get("family");
    const gradeName = searchParams.get("grade");
    const planId = searchParams.get("plan");
    const addonId = searchParams.get("addon");
    const coupon = searchParams.get("coupon");
    const skip = searchParams.get("skip");

    const { eukaEmailAddress } = useSettingsPageProps();

    const checkoutContext = useContext(CheckoutContext);

    const {
        productFamilies,
        userSelectedCountry,
        userSelectedLang,
        userSelectedState,
        currency
    } = checkoutContext;

    const [isLoading, setIsLoading] = useState(false);
    const [isInvalidEmail, setIsInvalidEmail] = useState(false);

    const [checkout, setCheckout] = useAtom(checkoutAtom);
    const setUserCheckoutStateToken = useSetAtom(userCheckoutStateTokenAtom);
    const setCanShowExistingUserPage = useSetAtom(canShowExistingUserPageAtom);
    const setCoupon = useSetAtom(couponAtom);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const setIsProvidedQueryParams = useSetAtom(isProvidedQueryParamsAtom);
    const [userCheckoutStateToken] = useAtom(userCheckoutStateTokenAtom);
    const [isNewsletterSubEnabled, setIsNewsletterSubEnabled] = useAtom(
        isNewsletterSubEnabledAtom
    );

    const auInfoFormSchema = useMemo(
        () =>
            Yup.object().shape({
                firstName: Yup.string()
                    .required(
                        t(
                            "nameValidationError",
                            "Parent's first name is required"
                        )
                    )
                    .matches(
                        nameRegex,
                        t(
                            "nameValidationRule",
                            "Parent's first name can only contain letters, spaces and some special characters: periods (.), apostrophes (') and hyphens (-)"
                        )
                    ),
                email: Yup.string().required(
                    t("emailValidationError", "Email address is required")
                )
            }),
        [i18n.language]
    );

    const infoFormSchema = useMemo(
        () =>
            Yup.object().shape({
                firstName: Yup.string().required(
                    t("nameValidationError", "Parent's first name is required")
                ),
                email: Yup.string().required(
                    t("emailValidationError", "Email address is required")
                )
            }),
        [i18n.language]
    );

    const [parentId] = useAtom(parentIdAtom);

    const { students } = usePage().props;
    const studentList =
        students &&
        (students as UserInterface[]).filter(student => student.is_enrolled);

    const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);
    const [pathNavigateTo, setPathNavigateTo] = useState(() => {
        // TODO: Improve hardcorded check for contries don't need state selections
        if (userSelectedCountry === "VN") {
            return CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE;
        }
        return CheckoutPageRoutesEnum.STATE_SELECT_PAGE;
    });

    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();
    const { checkoutStateMutationState, checkoutStateMutate } =
        usePostCheckoutState(() => {
            navigate(routeWithQueryParams(pathNavigateTo));
        });

    const {
        handleSubmit,
        register,
        setValue,
        trigger,
        setError,
        formState: { isValid, errors, touchedFields }
    } = useForm<UserInfoFormData>({
        defaultValues: {
            firstName: checkout.user.firstName ?? "",
            email: checkout.user.email ?? ""
        },
        resolver: yupResolver(
            userSelectedCountry === "AU" ? auInfoFormSchema : infoFormSchema
        ),
        reValidateMode: "onChange",
        mode: "onChange"
    });

    const onCaptchaVerified = (r: boolean) => setIsCaptchaVerified(r);

    const onSaveData = (userDetails: { firstName: string; email: string }) => {
        let productFamily: ProductFamilyInterface | null = null;
        let grade: GradeInterface | null = null;
        let plan: PlanInterface | null = null;
        let addon: AddonInterface | null = null;

        /**
         * We try to construct the checkout state
         * from the data passed via query parameters.
         * Correctly generated checkout link with query parameter values is expected.
         */
        if (productFamilies !== null) {
            const mappedCheckoutAtom = mapQueryParamsDataToCheckoutAtom(
                {
                    addonId,
                    gradeName,
                    planId,
                    productFamilyId
                },
                productFamilies
            );

            productFamily = mappedCheckoutAtom.productFamily;
            grade = mappedCheckoutAtom.grade;
            plan = mappedCheckoutAtom.plan;
            addon = mappedCheckoutAtom.addon;
        }

        // Decide on which page the user will be navigated to based on the available checkout state data.
        const nextAttemptPage = (() => {
            const hasAddons = !!plan?.addons;
            const addonsIncluded =
                plan?.addons?.every(a => a.isIncluded) ?? false;

            if (
                origin === "ecommerce" &&
                studentList &&
                skip === FLAG_STATUS.FALSE
            ) {
                setPathNavigateTo(
                    CheckoutPageRoutesEnum.STUDENT_SELECTION_PAGE
                );
            }

            // Early return for addon page selection based on specific conditions
            if (
                addon ||
                (skip === FLAG_STATUS.TRUE && hasAddons && !addonsIncluded)
            ) {
                setPathNavigateTo(CheckoutPageRoutesEnum.ADDON_SELECT_PAGE);
                return CheckoutPagesEnum.ADDON_SELECT_PAGE;
            }

            // Navigate to the payment page if skipping with no required addons
            if (
                (skip === FLAG_STATUS.TRUE && addonsIncluded) ||
                (skip === FLAG_STATUS.TRUE && !hasAddons)
            ) {
                setPathNavigateTo(CheckoutPageRoutesEnum.PAYMENT_PAGE);
                return CheckoutPagesEnum.PAYMENT_PAGE;
            }
            if (skip === FLAG_STATUS.FALSE || userSelectedCountry) {
                // TODO: Improve hardcorded check for contries don't need state selections
                if (userSelectedCountry === "VN") {
                    setPathNavigateTo(
                        CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                    );
                    return CheckoutPagesEnum.PRODUCT_FAMILY_SELECT_PAGE;
                }
                setPathNavigateTo(CheckoutPageRoutesEnum.STATE_SELECT_PAGE);
                return CheckoutPagesEnum.STATE_SELECT_PAGE;
            }

            // Navigate to the plan selection page based on skipping status or productFamily/grade specification
            if (skip === FLAG_STATUS.FALSE || (productFamily && grade)) {
                setPathNavigateTo(
                    CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                );
                return CheckoutPagesEnum.PRODUCT_FAMILY_SELECT_PAGE;
            }
        })();

        /**
         * Handling the default available products
         */
        let defaultAddon = addon;
        if (plan && !addon) {
            const availableAddons = plan.addons;
            if (availableAddons) {
                defaultAddon =
                    availableAddons.find(a => !a.isHighlighted) ?? null;
            }
        }

        setCheckout(prev => ({
            ...prev,
            productFamily,
            grade,
            plan,
            addon: defaultAddon,
            nextAttemptPage:
                nextAttemptPage ?? CheckoutPagesEnum.STATE_SELECT_PAGE
        }));

        // Update the related query parameters with the selected grade and family.
        onSetupQueryParams({
            ...pageQueryParams,
            addon: addon ? addon.id : null,
            plan: plan ? plan.id : null,
            family: productFamily ? productFamily.id : null,
            grade: grade ? grade.name : null,
            selectedCountry: userSelectedCountry ?? null,
            selectedLang: userSelectedLang ?? null,
            selectedState: userSelectedState ?? null,
            coupon
        });

        setUserCheckoutStateToken(null);
        setCoupon(coupon);

        dispatchGAEvents({
            event: "add_personal_details",
            personalData: {
                parent_first_name: userDetails.firstName,
                parent_email: userDetails.email
            },
            parentId
        });

        checkoutStateMutate();
    };

    const onSubmitUserInfoForm = async ({
        firstName,
        email
    }: UserInfoFormData) => {
        try {
            setIsLoading(true);
            const emailValidation = (
                await validateEmailApi(email, emailAbstractApiKey)
            ).data as {
                deliverability: "UNDELIVERABLE" | "DELIVERABLE";
                is_mx_found: { value: boolean };
                is_smtp_valid: { value: boolean };
            };

            // Note: Disabling the Email validation check when MX record does not exist
            // as outlook, hotmail are having some issues
            // const isValidEmail =
            //     emailValidation.deliverability !== "UNDELIVERABLE" &&
            //     emailValidation.is_mx_found.value;

            let isValidEmail =
                emailValidation.deliverability === "DELIVERABLE" ||
                emailValidation.is_mx_found.value; // When status is Undeliverable check if the domain exists and pass.

            let emailMsg = "This email address does not exist. Please check for any spelling mistakes.";

            if (isValidEmail) {
                const emailRegex = /[/]/;
                if (emailRegex.test(email)) {
                    isValidEmail = false;
                    emailMsg = "The email address must not contain a forward slash (/). Please enter a different email address.";
                }
            }

            if (!isValidEmail) {
                if (window.heap?.track) {
                    window.heap.track("UserInfo Error", {
                        error: "Invalid Email",
                        email,
                        firstName
                    });
                }
                setError(
                    "email",
                    {
                        message: emailMsg
                    },
                    { shouldFocus: true }
                );
                setIsInvalidEmail(true);
                return;
            }
            setIsInvalidEmail(false);

            if (window.heap?.track) {
                window.heap.identify(checkout.user.email);
                window.heap.addUserProperties({
                    email,
                    Name: firstName
                });
            }
        } catch (error) {
            console.error(error);
            setIsInvalidEmail(false);
            return;
        } finally {
            setIsLoading(false);
        }

        dispatchHSEvents(
            "identify",
            undefined,
            {
                firstName,
                email,
                newsletter_subscription: isNewsletterSubEnabled ? "yes" : "no",
                checkout_token: userCheckoutStateToken,
                selected_country: userSelectedCountry,
                hs_language: userSelectedLang
            },
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );

        dispatchHSEvents(
            "trackCustomBehavioralEvent",
            undefined,
            {
                checkout_status: "ABANDON",
                email: checkout.user.email ?? email,
                checkout_token: userCheckoutStateToken,
                newsletter_subscription: isNewsletterSubEnabled ? "yes" : "no",
                checkout_indicative_plan: pageQueryParams.plan,
                checkout_grade: pageQueryParams.grade,
                selected_country: userSelectedCountry,
                hs_language: userSelectedLang,
                checkout_is_test_record: checkout.user.email
                    .toLowerCase()
                    .includes(eukaEmailAddress)
                    ? "true"
                    : "false"
            },
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );

        setIsLoading(true);

        checkExistingUserApi({ email })
            .then(response => {
                const resData = response.data.data;

                // Existing parent - email not found in our system
                if (resData.isExisting) {
                    setCanShowExistingUserPage(true);
                    setIsLoading(false);

                    setCheckout(prev => ({
                        ...prev,
                        productFamily: null,
                        plan: null,
                        grade: null,
                        addon: null,
                        user: {
                            firstName: "",
                            email: "",
                            receiveOfferNews: false
                        }
                    }));

                    dispatchGAEvents({
                        event: "drop_off",
                        eventCategory: "checkout",
                        eventAction: "existing_user",
                        eventLabel: "User Details",
                        parentId
                    });

                    navigate("/checkout/existing-user");
                } else {
                    setCheckout(prev => ({
                        ...prev,
                        productFamily: null,
                        plan: null,
                        grade: null,
                        addon: null,
                        user: {
                            firstName,
                            email,
                            receiveOfferNews: isNewsletterSubEnabled
                        }
                    }));
                }

                dispatchHSEvents(
                    "identify",
                    undefined,
                    {
                        firstName,
                        email,
                        selected_country: userSelectedCountry,
                        hs_language: userSelectedLang,
                        newsletter_subscription: isNewsletterSubEnabled
                            ? "yes"
                            : "no",
                        checkout_token: userCheckoutStateToken
                    },
                    "checkout",
                    hubspotEventCheckout,
                    hubspotEventEnrol
                );

                dispatchHSEvents(
                    "trackCustomBehavioralEvent",
                    undefined,
                    {
                        checkout_status: "ABANDON",
                        firstName,
                        email,
                        selected_country: userSelectedCountry,
                        hs_language: userSelectedLang,
                        checkout_token: userCheckoutStateToken,
                        newsletter_subscription: isNewsletterSubEnabled
                            ? "yes"
                            : "no",
                        checkout_indicative_plan: pageQueryParams.plan,
                        checkout_grade: pageQueryParams.grade
                    },
                    "checkout",
                    hubspotEventCheckout,
                    hubspotEventEnrol
                );

                setIsLoading(false);
                onSaveData({ email, firstName });
            })
            .catch(() => setIsLoading(false));
    };

    const handleChange = (
        e: ChangeEvent<HTMLInputElement>,
        setValue: (
            name: keyof UserInfoFormData,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value: any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            options?: any
        ) => void
    ) => {
        const { name, value } = e.target;

        setValue(name as keyof UserInfoFormData, value, {
            shouldValidate: true
        });
    };

    useEffect(() => {
        setIsNewsletterSubEnabled(checkout.user.receiveOfferNews);
        setValue("email", checkout.user.email, { shouldValidate: true });
        setValue("firstName", checkout.user.firstName, {
            shouldValidate: true
        });
    }, [checkout]);

    useEffect(() => {
        trigger("firstName");
        trigger("email");
    }, [trigger, setValue]);

    useEffect(() => {
        // Set all the query parameters in the URL which were updated in "pageQueryParams" atom at this point.
        setSearchParams(removeNullProperties(pageQueryParams));
        setIsProvidedQueryParams(false);
    }, [pageQueryParams]);

    useEffect(() => {
        const isUserInfoPageCompleted =
            !!checkout.user.email && !!checkout.user.firstName;
        if (isParentUserLoggedIn && pageQueryParams.origin === "ecommerce") {
            setIsProvidedQueryParams(false);
            navigate(CheckoutPageRoutesEnum.STUDENT_SELECTION_PAGE);
        } else if (isUserInfoPageCompleted) {
            // TODO: Improve hardcorded check for contries don't need state selections
            navigate(
                userSelectedCountry === "VN"
                    ? CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                    : CheckoutPageRoutesEnum.STATE_SELECT_PAGE
            );
        }
    }, []);

    // Note: User can change country & language only on the user info page.
    useEffect(() => {
        dispatchGAEvents({
            event: "begin_checkout",
            currency,
            location: userSelectedCountry,
            language: userSelectedLang,
            parentId
        });
    }, [userSelectedCountry, userSelectedLang]);

    return (
        <ContentLayout
            title={t(
                "checkout.authPage.title",
                "Let's start with some details about you"
            )}
            step={1}
            className={ClassesEnum.UserInfoContainer}
        >
            <Text textAlign={["left", "center"]}>
                {t(
                    "checkout.authPage.desc",
                    "To start the enrolment process, we'll need some information about you"
                )}
            </Text>
            <Flex direction="column" as="form" gap={6}>
                <FormControl
                    className={ClassesEnum.FormField}
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.UserInfoContainer, ClassesEnum.FormField],
                        "firstNameInput"
                    )}
                    isInvalid={!!errors.firstName && touchedFields["firstName"]}
                >
                    <FormLabel>
                        {t(
                            "checkout.authPage.firstNameLabel",
                            "Parent's first name"
                        )}
                    </FormLabel>
                    <Input
                        data-testid="first-name-input"
                        className={ClassesEnum.Input}
                        data-tracking-id={getTrackingId(
                            [
                                ClassesEnum.UserInfoContainer,
                                ClassesEnum.FormField,
                                ClassesEnum.Input
                            ],
                            "firstNameInput"
                        )}
                        id="firstName"
                        {...register("firstName")}
                        onChange={e => handleChange(e, setValue)}
                        maxLength={50}
                    />
                    <FormErrorMessage>
                        {t(errors.firstName?.message as string)}
                    </FormErrorMessage>
                </FormControl>
                <FormControl
                    className={ClassesEnum.FormField}
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.UserInfoContainer, ClassesEnum.FormField],
                        "emailInput"
                    )}
                    fontSize={15}
                    isInvalid={!!errors.email && touchedFields["email"]}
                >
                    <FormLabel>
                        {t(
                            "checkout.authPage.emailLabel",
                            "Parent's email address"
                        )}
                    </FormLabel>
                    <Input
                        data-testid="email-input"
                        className={ClassesEnum.Input}
                        data-tracking-id={getTrackingId(
                            [
                                ClassesEnum.UserInfoContainer,
                                ClassesEnum.FormField,
                                ClassesEnum.Input
                            ],
                            "emailInput"
                        )}
                        id="email"
                        {...register("email", {
                            onChange: e => {
                                handleChange(e, setValue);
                                setIsInvalidEmail(false);
                            }
                        })}
                        placeholder="example@gmail.com"
                        maxLength={200}
                    />
                    <FormErrorMessage>
                        {t(errors.email?.message as string)}
                    </FormErrorMessage>
                </FormControl>
            </Flex>
            <Flex direction="column" gap={8}>
                <Checkbox
                    data-testid="checkNewsletterSubscription"
                    className={ClassesEnum.Checkbox}
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.UserInfoContainer, ClassesEnum.Checkbox],
                        "checkNewsletterSubscription"
                    )}
                    isChecked={isNewsletterSubEnabled}
                    onChange={() =>
                        setIsNewsletterSubEnabled(!isNewsletterSubEnabled)
                    }
                >
                    {t(
                        "checkout.authPage.promotionCheckbox",
                        "Receive free access to useful resources, tips and offers about online education"
                    )}
                </Checkbox>
                <Button
                    data-testid="next-btn"
                    className={ClassesEnum.Button}
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.UserInfoContainer, ClassesEnum.Button],
                        "goNext"
                    )}
                    isLoading={
                        checkoutStateMutationState.isPending || isLoading
                    }
                    onClick={handleSubmit(onSubmitUserInfoForm)}
                    isDisabled={
                        !isValid ||
                        isLoading ||
                        !isCaptchaVerified ||
                        !productFamilies?.length ||
                        isInvalidEmail
                    }
                    w="100%"
                    colorScheme="ebonyClay"
                >
                    {t("footer.nextBtn", "Next")}
                </Button>
                <Flex justify="center" gap={2} align="center">
                    <Text>
                        {t(
                            "checkout.authPage.alreadyHaveAccountText",
                            "Already have an account?"
                        )}
                    </Text>
                    <Link href={route("login")} data-testid="sign-in-btn">
                        <Button
                            className={ClassesEnum.Button}
                            data-tracking-id={getTrackingId(
                                [
                                    ClassesEnum.UserInfoContainer,
                                    ClassesEnum.Button
                                ],
                                "signIn"
                            )}
                            fontSize={[14, 16]}
                            variant="link"
                            pl={2}
                            pr={2}
                        >
                            {t("checkout.authPage.signInBtn", "Sign In")}
                        </Button>
                    </Link>
                </Flex>
            </Flex>
            <ReCaptcha
                isCaptchaVerified={isCaptchaVerified}
                onCaptchaVerified={onCaptchaVerified}
            />
        </ContentLayout>
    );
};

export default UserInfoContainer;
